import { Injectable } from "@angular/core";
import { BehaviorSubject, from, Observable, of, throwError } from "rxjs";
import { flow } from "ramda";
import { catchError, map } from "rxjs/operators";
import {
  Category,
  CategoryWithMenu,
  Constants,
  CreateCategoryParam,
  CreateCustomisation,
  CreateMenuItemParam,
  CreateMenuParam,
  CreateModifier,
  DriaryAttribute,
  FoodCustomisation,
  FoodItem,
  FoodItemDTO,
  FoodItemTemperature,
  FoodService,
  MenuItemConfigParam,
  MenuProfile,
  MenuShallowProfile,
  MerchantCategory,
  Modifier,
  PriceBy,
  RestaurantProfile,
  RestaurantStatus,
  RestaurantStatuses,
  UpdateRestauranProfileParam,
} from "./food.service.base";
import { MerchantService } from "./merchant.service";
import { HttpRequestsService } from "../http-requests.service";
import { MERCHANT_ROUTES } from "src/app/constants/constants";
import { environment } from "src/environments/environment";
import { formDataTrait } from "src/app/utils/form-data";
import * as apiTranslator from "./food.service.api.translator";
import { CreateMenuDto, MenuDto } from "./food.service.api.dto";
import { DataService } from "../cache-layer.service";
import { HttpParams } from "@angular/common/http";
import { match } from "ts-pattern";
import { OrderStatuses } from "src/app/pages/merchant/food-order/models";

@Injectable({
  providedIn: "root",
})
export class ApiFoodService extends FoodService {
  private foodServiceHost: string = environment.FOOD_SERVICE;

  customisations: FoodCustomisation[] = [
    {
      id: "1",
      name: "Extra Cheese",
      note: "Extra cheese on top of the pizza",
    },
  ];

  constructor(
    private merchantService: MerchantService,
    private http: HttpRequestsService,
    private cacheDataService: DataService,
  ) {
    super();
  }

  protected isFoodMerchant: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );

  protected categoryLists: BehaviorSubject<CategoryWithMenu[] | null> =
    new BehaviorSubject(null);

  protected configurations: BehaviorSubject<MenuItemConfigParam | null> =
    new BehaviorSubject(null);

  checkIfFoodMerchant() {
    from(this.merchantService.getProfile())
      .pipe(
        map((userProfile) => {
          this.isFoodMerchant.next(userProfile.isSmartieFoods);
        }),
      )
      .subscribe();
    return this.isFoodMerchant;
  }

  async getRestaurantProfile(): Promise<RestaurantProfile | null> {
    const apiDtoRestaurant = await this.http.rawGet(
      this.foodServiceHost,
      MERCHANT_ROUTES.GET_DEFAULT_RESTAURANT,
    );
    return (
      apiDtoRestaurant &&
      apiTranslator.translateToRestaurantProfile(apiDtoRestaurant)
    );
  }

  async saveRestaurantProfile(
    updateData: RestaurantProfile,
    logo: File,
  ): Promise<void> {
    const data = flow(new FormData(), [
      formDataTrait.guardedSet("email", updateData.email, (email) => !!email),
      formDataTrait.guardedSet("city", updateData.address?.city),
      formDataTrait.guardedSet("country", updateData.address?.country),
      formDataTrait.guardedSet("state", updateData.address?.state),
      formDataTrait.guardedSet("street", updateData.address?.address),
      formDataTrait.guardedSet("name", updateData.name),
      formDataTrait.guardedSet("contactNumber", updateData.phoneNumber),
      formDataTrait.guardedSet("timezone", updateData.timezone),
      formDataTrait.guardedSet("logoImage", logo),
      formDataTrait.guardedSet("latitude", updateData.address?.lat?.toString()),
      formDataTrait.guardedSet(
        "longitude",
        updateData.address?.long?.toString(),
      ),
      ...updateData.cuisines.map((cuisineSlug, index) => {
        return formDataTrait.guardedSet(
          `merchantCategorySlugs[${index}]`,
          cuisineSlug,
        );
      }),
    ]);
    await this.http.rawPatch(
      this.foodServiceHost,
      MERCHANT_ROUTES.UPDATE_RESTAURANT_DEFAULT,
      data,
      false,
    );
  }

  async becomeFoodMerchant(
    restaurantProfile: UpdateRestauranProfileParam,
  ): Promise<void> {
    const data = flow(new FormData(), [
      formDataTrait.guardedSet("email", restaurantProfile.email),
      formDataTrait.guardedSet("city", restaurantProfile.address.city),
      formDataTrait.guardedSet("country", restaurantProfile.address.country),
      formDataTrait.guardedSet("state", restaurantProfile.address.state),
      formDataTrait.guardedSet("street", restaurantProfile.address.address),
      formDataTrait.guardedSet("name", restaurantProfile.name),
      formDataTrait.guardedSet("phoneNumber", restaurantProfile.phoneNumber),
      formDataTrait.guardedSet("timezone", restaurantProfile.timezone),
      formDataTrait.guardedSet("logo", restaurantProfile.logo),
      formDataTrait.guardedSet(
        "latitude",
        restaurantProfile.address.lat?.toString(),
      ),
      formDataTrait.guardedSet(
        "longitude",
        restaurantProfile.address.long?.toString(),
      ),
      ...restaurantProfile.cuisines.map((cuisineSlug, index) => {
        return formDataTrait.guardedSet(
          `categoryCuisineSlugs[${index}]`,
          cuisineSlug,
        );
      }),
    ]);
    await this.http.rawPost(
      this.foodServiceHost,
      MERCHANT_ROUTES.BECOME_FOOD_MERCHANT,
      data,
      false,
    );
    this.isFoodMerchant.next(true);
  }

  async createNewMenu(createDto: CreateMenuParam) {
    const menuDto: CreateMenuDto = apiTranslator.convertToMenuDto(createDto);

    try {
      // Make the HTTP POST request
      const response: MenuDto = await this.http.rawPost<MenuDto>(
        this.foodServiceHost,
        MERCHANT_ROUTES.ADD_NEW_MENU,
        menuDto,
      );

      // Return the API response as MenuProfile
      return apiTranslator.mapMenuDtoToMenuProfile(response);
    } catch (error) {
      console.error("Error creating new menu:", error);
      throw error; // Re-throw the error after logging it
    }
  }

  getMenuDetail(id: string): Observable<MenuProfile> {
    // cache stale-while-validate
    return this.cacheDataService
      .getData(`menu-detail-${id}`, () =>
        this.http.rawGet<MenuProfile>(
          this.foodServiceHost,
          `${MERCHANT_ROUTES.GET_MENU}/${id}`,
        ),
      )
      .pipe(
        map((response) => {
          if (response) {
            return response;
          } else {
            throw new Error("No response received");
          }
        }),
        catchError((error) => {
          console.error("Error fetching menu detail:", error);
          return throwError(() => error);
        }),
      );
  }

  getMenuList(): Observable<Array<MenuShallowProfile>> {
    // cache stale-while-validate
    return this.cacheDataService
      .getData("menuList", () =>
        this.http.rawGet<any[]>(
          this.foodServiceHost,
          MERCHANT_ROUTES.GET_DEFAULT_MENU_LIST,
        ),
      )
      .pipe(
        map((rawMenus) => {
          console.log("raw menus", rawMenus);
          return rawMenus.map(apiTranslator.mapMenuDtoToMenuProfile);
        }),
      );
  }

  async updateMenu(menu: MenuProfile): Promise<MenuProfile> {
    try {
      const response: any = await this.http.rawPut(
        this.foodServiceHost,
        `${MERCHANT_ROUTES.UPDATE_MENU}/${menu.id}`,
        menu,
      );
      return response;
    } catch (error) {
      console.error("Error creating customisation:", error);
      throw error;
    }
  }

  getCategoryList(): Observable<CategoryWithMenu[]> {
    return this.cacheDataService
      .getData("category-list", () =>
        this.http.rawGet<CategoryWithMenu[]>(
          this.foodServiceHost,
          MERCHANT_ROUTES.GET_MENU_CATEGORIES,
        ),
      )
      .pipe(
        catchError((error) => {
          console.error("Error getting categories:", error);
          return throwError(() => error);
        }),
      );
  }

  getItemDetail(id: string): Observable<FoodItem | null> {
    return this.cacheDataService
      .getData(`item-detail-${id}`, () =>
        this.http.rawGet(
          this.foodServiceHost,
          `${MERCHANT_ROUTES.MENU_ITEM}/${id}`,
        ),
      )
      .pipe(map(apiTranslator.mapItemDtoToFoodItem))
      .pipe(
        catchError((error) => {
          console.error("Error getting categories:", error);
          return throwError(() => error);
        }),
      );
  }

  private menuItemParamsToFormData(
    menuItem: CreateMenuItemParam & { id?: string },
  ) {
    const formData = new FormData();
    // Append all form values to FormData
    Object.keys(menuItem).forEach((key) => {
      if (key === "profileImageUrl") {
        if (menuItem[key] instanceof File) {
          formData.append("itemImage", menuItem[key], menuItem[key].name);
        }
      } else if (key === "categoryIds") {
        // Ensure categoryIds is an array
        // Append each categoryId separately
        menuItem.categoryIds.forEach((id: string, index: number) => {
          formData.append(`categoryIds[${index}]`, id);
        });
      } else if (key === "dietaryAttributes") {
        menuItem.dietaryAttributes.forEach((attr: string, index: number) => {
          formData.append(`dietaryAttributes[${index}]`, attr);
        });
      } else {
        formData.append(key, menuItem[key]);
      }
    });
    return formData;
  }

  updateMenuItem(
    menuItem: CreateMenuItemParam & { id: string },
  ): Observable<FoodItem> {
    const url = `${MERCHANT_ROUTES.MENU_ITEM}/${menuItem.id}`;
    const formData = this.menuItemParamsToFormData(menuItem);
    return from(
      this.http.rawPut<FoodItem>(this.foodServiceHost, url, formData),
    ).pipe(
      catchError((error) => {
        console.error("Error updating food item:", error);
        return throwError(() => error);
      }),
    );
  }

  createMenuItem(menuItem: CreateMenuItemParam): Observable<FoodItem> {
    const formData = this.menuItemParamsToFormData(menuItem);
    return from(
      this.http.rawPost<FoodItem>(
        this.foodServiceHost,
        MERCHANT_ROUTES.MENU_ITEM,
        formData,
        undefined,
        undefined,
        false,
      ),
    ).pipe(
      catchError((error) => {
        console.error("Error creating food item:", error);
        return throwError(error);
      }),
    );
  }

  getConfiguration(): Observable<MenuItemConfigParam> {
    if (!this.configurations.getValue()) {
      this.configurations.next({
        foodItemTemperatures: Object.keys(FoodItemTemperature).map((k) => ({
          name: FoodItemTemperature[k],
          id: k,
        })),
        priceByOptions: Object.keys(PriceBy).map((k) => ({
          name: PriceBy[k],
          id: k,
        })),
        dietaryAttributes: Object.keys(DriaryAttribute).map((k) => ({
          name: DriaryAttribute[k],
          id: k,
        })),
      });
    }
    return this.configurations.asObservable();
  }
  getFoodItems(params: {
    page: number;
    search?: string;
    limit: number;
    categoryId?: string;
  }): Observable<{
    meta: {
      total: number;
      page: number;
      limit: number;
    };
    data: FoodItemDTO[];
  }> {
    let httpParams = new HttpParams()
      .set("page", params.page.toString())
      .set("limit", params.limit.toString());

    if (params.categoryId) {
      httpParams = httpParams.set("categoryIds", params.categoryId);
    }

    if (params.search) {
      httpParams = httpParams.set("search", params.search);
    }

    const url = `${MERCHANT_ROUTES.MENU_ITEM}?${httpParams.toString()}`;

    return from(
      this.http.rawGet<{
        meta: {
          total: number;
          page: number;
          limit: number;
        };
        data: FoodItemDTO[];
      }>(this.foodServiceHost, url),
    ).pipe(
      map((response) => ({
        meta: response.meta,
        data: response.data,
      })),
      catchError((error) => {
        console.error("Error getting menu items:", error);
        throw error;
      }),
    );
  }

  addNewCategory(createDto: CreateCategoryParam): Observable<Category> {
    try {
      return from(
        this.http.rawPost<Category>(
          this.foodServiceHost,
          MERCHANT_ROUTES.CREATE_MENU_CATEGORY,
          createDto,
        ),
      ).pipe(
        map((response) => response),
        catchError((error) => {
          console.error("Error getting menu items:", error);
          throw error;
        }),
      );
    } catch (error) {
      console.error("Error creating new category:", error);
      throw error;
    }
  }
  updateCategory(category: Category): Observable<Category> {
    try {
      return from(
        this.http.rawPut(
          this.foodServiceHost,
          `${MERCHANT_ROUTES.UPDATE_MENU_CATEGORY}/${category.id}`,
          category,
        ),
      ).pipe(
        map((response) => response),
        catchError((error) => {
          console.error("Error updating category:", error);
          throw error;
        }),
      );
    } catch (error) {
      console.error("Error updating category:", error);
      throw error;
    }
  }

  getCategoryDetail(id: string): Observable<CategoryWithMenu> {
    try {
      return from(
        this.http.rawGet(
          this.foodServiceHost,
          `${MERCHANT_ROUTES.UPDATE_MENU_CATEGORY}/${id}`,
        ),
      ).pipe(
        map((response) => response),
        catchError((error) => {
          console.error("Error getting category details:", error);
          throw error;
        }),
      );
    } catch (error) {
      console.error("Error updating category:", error);
      throw error;
    }
  }

  async getCustomisations(params: {
    page: number;
    search: string;
    limit: number;
  }): Promise<{
    meta: {
      total: number;
      page: number;
      limit: number;
    };
    data: FoodCustomisation[];
  }> {
    try {
      // Construct the URL with parameters
      let url = `${MERCHANT_ROUTES.GET_CUSTOMISATIONS}?page=${params.page}&limit=${params.limit}`;

      // Add search parameter only if it's not empty
      if (params.search && params.search.trim() !== "") {
        url += `&search=${encodeURIComponent(params.search.trim())}`;
      }

      const response: any = await this.http.rawGet<MenuDto>(
        this.foodServiceHost,
        url,
      );

      return {
        meta: response.meta,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching customisations:", error);
      throw error;
    }
  }

  async createCustomisation(
    createCustomisation: CreateCustomisation,
  ): Promise<{ data: FoodCustomisation }> {
    try {
      const response: any = await this.http.rawPost<FoodCustomisation>(
        this.foodServiceHost,
        MERCHANT_ROUTES.CREATE_CUSTOMISATION,
        createCustomisation,
      );
      return {
        data: response.data,
      };
    } catch (error) {
      console.error("Error creating customisation:", error);
      throw error;
    }
  }

  async addModifierToModifierGroup(
    modifierGroupId: string,
    createModifier: CreateModifier,
  ): Promise<Modifier> {
    try {
      const response: Modifier = await this.http.rawPost<Modifier>(
        this.foodServiceHost,
        MERCHANT_ROUTES.CREATE_CUSTOMISATION +
          "/" +
          modifierGroupId +
          "/modifier",
        createModifier,
      );
      return response;
    } catch (error) {
      console.error("Error creating modifier:", error);
      throw error;
    }
  }

  async deleteCustomisation(
    modifierGroupId: string,
  ): Promise<{ success: boolean }> {
    try {
      const response: any = await this.http.delete(
        `${MERCHANT_ROUTES.CREATE_CUSTOMISATION}/${modifierGroupId}`,
      );
      return response;
    } catch (error) {
      console.error("Error deleting modifier group:", error);
      throw error;
    }
  }

  async deleteModifier(
    modifierGroupId: string,
    modifierId: string,
  ): Promise<{ success: boolean }> {
    try {
      const response: any = await this.http.delete(
        `${MERCHANT_ROUTES.CREATE_CUSTOMISATION}/${modifierGroupId}/modifier/${modifierId}`,
      );
      return response;
    } catch (error) {
      console.error("Error deleting modifier:", error);
      throw error;
    }
  }

  async updateModifierDisplayOrderIndex(
    modifierGroupId: string,
    modifiers: { id: string; orderIndex: number }[],
  ): Promise<FoodCustomisation> {
    try {
      const response: any = await this.http.rawPatch(
        this.foodServiceHost,
        `${MERCHANT_ROUTES.CREATE_CUSTOMISATION}/${modifierGroupId}/modifier/display-order`,
        { modifiers: modifiers },
      );
      return response;
    } catch (error) {
      console.error("Error updating modifier order:", error);
      throw error;
    }
  }

  async updateModifierGroupRules(
    modifierGroupId: string,
    rules: {
      minSelection: number;
      maxSelection: number;
      multipleSelections: boolean;
      maxQuantityOfEachModifier?: number;
    },
  ): Promise<FoodCustomisation> {
    try {
      const response: any = await this.http.rawPatch(
        this.foodServiceHost,
        `${MERCHANT_ROUTES.CREATE_CUSTOMISATION}/${modifierGroupId}/rules`,
        rules,
      );
      return response;
    } catch (error) {
      console.error("Error updating modifier rules:", error);
      throw error;
    }
  }

  async addCustomisationToitems(
    modifierGroupId: string,
    itemIds: string[],
  ): Promise<FoodCustomisation> {
    try {
      const res: any = await this.http.rawPatch(
        this.foodServiceHost,
        `${MERCHANT_ROUTES.CREATE_CUSTOMISATION}/${modifierGroupId}/add-items`,
        { itemIds },
      );
      console.log("Response from adding customisation to items:", res);
      return res;
    } catch (error) {
      console.error("Error adding customisation to items:", error);
      throw error;
    }
  }

  async updateItemCategoryOrder(
    categoryId: string,
    ids: string[],
  ): Promise<void> {
    await this.http.rawPatch(
      this.foodServiceHost,
      MERCHANT_ROUTES.UPDATE_CAT_ITEMS_ORDER.replace(":categoryId", categoryId),
      {
        itemOrders: ids.map((id, index) => ({
          itemId: id,
          order: index,
        })),
      },
    );
  }

  async updateCategoryOrder(menuId: string, catIds: string[]): Promise<void> {
    await this.http.rawPatch(
      this.foodServiceHost,
      MERCHANT_ROUTES.UPDATE_CAT_ORDER.replace(":menuId", menuId),
      {
        orders: catIds.map((id, index) => ({
          catId: id,
          order: index,
        })),
      },
    );
  }

  updateRestaurantStatus(
    status: RestaurantStatus,
  ): Observable<RestaurantStatus> {
    const utilDate = status.remainingTime
      ? new Date(Date.now() + status.remainingTime * 1000)
      : null;
    const updateDto = match(status.type)
      .with(RestaurantStatuses.OPEN, () => ({
        isOpen: true,
      }))
      .with(RestaurantStatuses.BUSY, () => ({
        isBusy: true,
        busyOrderDelay: status.addingTime,
      }))
      .with(RestaurantStatuses.PAUSED, () => ({
        isPaused: true,
        pauseEndTime: utilDate,
      }))
      .exhaustive();
    return from(
      this.http.rawPut(
        this.foodServiceHost,
        MERCHANT_ROUTES.RESTAURANT_STATUS,
        updateDto,
      ),
    ).pipe(map((response) => this.transformRestaurantStatus(response.data)));
  }

  getRestaurantStatus(): Observable<RestaurantStatus> {
    return from(
      this.http.rawGet(this.foodServiceHost, MERCHANT_ROUTES.RESTAURANT_STATUS),
    ).pipe(map((response) => this.transformRestaurantStatus(response)));
  }

  private transformRestaurantStatus(data: any): RestaurantStatus {
    const now = new Date();

    if (data.isPaused && data.pauseEndTime) {
      const remainingTime = Math.max(
        0,
        (new Date(data.pauseEndTime).getTime() - now.getTime()) / 1000,
      );
      return {
        type: RestaurantStatuses.PAUSED,
        remainingTime: Math.round(remainingTime),
        addingTime: null,
      };
    }

    if (data.isBusy) {
      const remainingTime = Math.max(
        0,
        (new Date(data.busyEndTime).getTime() - now.getTime()) / 1000,
      );
      return {
        type: RestaurantStatuses.BUSY,
        addingTime: data.busyOrderDelay,
        remainingTime,
      };
    }

    return {
      type: RestaurantStatuses.OPEN,
      remainingTime: null,
      addingTime: null,
    };
  }

  getConstants(): Observable<Constants> {
    return from(
      this.http.rawGet(this.foodServiceHost, MERCHANT_ROUTES.CONSTANT),
    ).pipe(
      map((response) => ({
        orderStatuses: (response.data.orderStatuses || []).map((status) => ({
          id: status,
          name: this.formatStatusName(status),
        })),
        timezones: (response.data.timezones || []).map((tz) => ({
          id: tz,
          name: tz,
        })),
      })),
    );
  }

  getAllMerchantCategory(): Observable<MerchantCategory[]> {
    return from(
      this.http.rawGet(this.foodServiceHost, MERCHANT_ROUTES.MERCHANT_CATEGORY),
    ).pipe(
      map((response) =>
        (response.data || []).map((catRes) => ({
          name: catRes.name,
          slug: catRes.slug,
        })),
      ),
    );
  }

  private formatStatusName(status: string): string {
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }
}
